<script setup>
// Astra DS · SendRecord — el buzón.
//
// Figma: Pattern/SendRecord 699:511. En la Home entra como el bloque `demos`
// (Template/Home-Reset 730:7237 › 730:7305).
//
// «El buzón. Único bloque invertido de la página: si hay dos momentos en
// negativo, ninguno es el importante. Lista las cuatro cosas que pide el
// formulario, que son las que llenan la puerta de demos y la ficha del
// artista.»
//
// El titular es el único de la casa que NO va en caja alta —`display/invite`—
// porque es una frase dicha, no un rótulo.
//
// Hasta que haya formulario, la llamada es un `mailto:`. Está decidido así en
// REFACTOR.md § Alcance funcional nuevo, no es un provisional sin dueño.
defineProps({
  // El buzón dice lo mismo esté donde esté, así que su texto vive AQUÍ y no
  // en cada vista: lo montan la portada y «Sobre Astra FM», y escribirlo dos
  // veces era la vía rápida a que acabaran diciendo cosas distintas. Se puede
  // sobrescribir, pero de momento nadie lo hace.
  kicker: { type: String, default: "El buzón está abierto" },
  title: { type: String, default: "Manda tu disco" },
  body: {
    type: String,
    default:
      "No hace falta sello, ni nota de prensa, ni contacto dentro. Lo escuchamos todo y contestamos a lo que ponemos. Si entra, entra en la parrilla, no en una lista de espera.",
  },
  cta: { type: String, default: "Enviar un disco" },

  // ⚠️ Dos props y no una: `ctaHref` es para lo que sale de la aplicación —el
  // `mailto`, un enlace externo— y `ctaTo` para una ruta de dentro. Pasar una
  // ruta interna por `href` la convierte en una recarga completa de la página
  // en una SPA, y se nota.
  ctaHref: { type: String, default: "mailto:info@astra.fm" },
  ctaTo: { type: [String, Object], default: null },

  // Lo que pedimos: { label, value }. Cuatro celdas que reparten el alto.
  items: {
    type: Array,
    default: () => [
      { label: "Nos mandas", value: "Portada, título, ciudad y formato" },
      { label: "Y dos frases", value: "Qué es y de dónde sale" },
      { label: "Si tenéis", value: "Próximas fechas y qué viene después" },
      { label: "Nada más", value: "Con eso ya tenéis ficha en la web" },
    ],
  },
});
</script>

<template>
  <section class="send" aria-label="Manda tu disco">
    <div class="send__text">
      <p class="send__kicker">{{ kicker }}</p>
      <h2 class="send__title">{{ title }}</h2>
      <p v-if="body" class="send__body">{{ body }}</p>

      <router-link v-if="ctaTo" class="send__cta" :to="ctaTo">
        {{ cta }}
        <span aria-hidden="true">↗</span>
      </router-link>

      <a v-else class="send__cta" :href="ctaHref">
        {{ cta }}
        <span aria-hidden="true">↗</span>
      </a>
    </div>

    <dl v-if="items.length" class="send__asks">
      <div v-for="item in items" :key="item.label" class="send__ask">
        <dt>{{ item.label }}</dt>
        <dd>{{ item.value }}</dd>
      </div>
    </dl>
  </section>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

// Invertido de arriba abajo: el bloque entero cambia de tinta, no solo el
// fondo.
//
// ⚠️ Las líneas de dentro NO pueden ser `--color-border-inverse`: ese token
// resuelve al mismo valor que `--color-bg-inverse` en los dos temas
// —neutral-900 en claro, neutral-100 en oscuro— así que la línea desaparece
// contra su propio fondo. El nodo de Figma (699:511) las tiene igual y por eso
// allí tampoco se ven. El prototipo sí lo resuelve: lienzo al 22% sobre la
// tinta. Se hace aquí lo mismo, derivándolo del token de texto invertido en vez
// de escribir un rgba a mano. Anotado como discrepancia abierta en REFACTOR.md.
.send {
  --send-rule: color-mix(in srgb, var(--color-text-inverse) 22%, transparent);
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  box-sizing: border-box;
  width: 100%;
  // Sin cierre propio: la junta con el módulo de abajo la pone la pila de la
  // vista (`scss/_estructura.scss`). Dibujarla aquí la duplicaba.
  background: var(--color-bg-inverse);
  color: var(--color-text-inverse);
}

.send__text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
  padding: 34px 28px 32px;
  border-right: 1px solid var(--send-rule);
}

.send__kicker {
  @include t.mono-label;
  margin: 0;
  opacity: 0.6;
}

.send__title {
  @include t.display-invite;
  margin: 0;
}

// Medida de lectura corta: es una invitación de cuatro líneas, no un artículo.
.send__body {
  @include t.body-prose;
  max-width: 48ch;
  margin: 0;
  opacity: 0.78;
}

.send__cta {
  @include t.mono-label;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 13px 20px;
  background: var(--color-bg-accent);
  color: var(--color-text-on-action);
  text-decoration: none;
  transition:
    background 0.12s linear,
    color 0.12s linear;

  // Al pasar, la llamada se invierte contra el bloque invertido: pasa al
  // lienzo. Es el único sitio donde el rojo cede, y se nota porque alrededor
  // todo es tinta.
  &:hover,
  &:focus-visible {
    background: var(--color-bg-canvas);
    color: var(--color-text-primary);
  }
}

.send__asks {
  display: flex;
  flex-direction: column;
  margin: 0;
}

// Las cuatro celdas reparten el alto del bloque, que lo manda la columna de
// texto. `flex: 1` y no altura fija: el titular es fluido y el bloque crece.
.send__ask {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--send-rule);

  &:last-child {
    border-bottom: 0;
  }

  dt {
    @include t.mono-nano;
    margin: 0;
    opacity: 0.55;
  }

  dd {
    @include t.mono-para;
    margin: 0;
  }
}

// Por debajo del ancho de la retícula la columna de peticiones deja de caber
// al lado y pasa debajo. El filete que las separaba cambia de lado con ellas.
@media (max-width: 980px) {
  .send {
    grid-template-columns: 1fr;
  }

  .send__text {
    border-right: 0;
    border-bottom: 1px solid var(--send-rule);
  }
}

@media (prefers-reduced-motion: reduce) {
  .send__cta {
    transition: none;
  }
}

// ── El margen móvil ──
//
// 20 a cada lado, como el resto del sistema por debajo de 880. Este bloque se
// había quedado con el de escritorio, y en una pantalla de 390 convivían tres
// verticales distintas —20, 24 y 28— según de qué componente fuera cada caja.
// Se lee como desalineación aunque cada bloque, por separado, esté bien.
//
// Mismo corte que ModuleTitleBar, que era el único que ya lo hacía.
@media (max-width: 880px) {
  .send__text {
    padding: 28px 20px 26px;
  }
}
</style>
